import { FC, memo, useMemo } from 'react'
import { Card, CardProps, Flex, Divider, Stack, HStack } from '@chakra-ui/react'

import { Salary, SalaryFieldsName, SalaryFilterKeyString, SalaryKeyString } from 'InstitutionalManagement/types'
import { CustomIconViewer, NoveltyBadge, Status, TextWithCharacterLimit } from 'components'
import { SALARY_INITIAL_FILTER_FIELDS } from 'InstitutionalManagement/constants/pages/salaries'
import { OwnerIcon } from 'components/visualHelps'
import { centsToReais } from 'utils/transforms/financial'

type Props = CardProps & {
  hideHeader?: boolean
  onlyFields?: SalaryFieldsName[]
  salaryFilter?: SalaryFilterKeyString
  salary: Salary
}

const SalaryCard: FC<Props> = ({ hideHeader = false, onlyFields, salary, salaryFilter, children, ...cardProps }) => {
  const SalaryFilterFields = useMemo(() => {
    return onlyFields || SALARY_INITIAL_FILTER_FIELDS
  }, [])

  function setSalaryValueByKey(key: string): string {
    const salaryWithKeyString: SalaryKeyString = { ...salary }
    return (salaryWithKeyString[`salary_${key}`] || salaryWithKeyString[key]) as string
  }

  function setSalaryFilterValueByKey(key: string): string {
    if (!salaryFilter) return ''
    return salaryFilter[`salary_${key}`] || salaryFilter[key]
  }

  console.log('salary: ', salary)

  return (
    <Card p={2} width="100%" height="100%" className="accessible" _hover={{ bg: 'lightPrimary' }} {...cardProps}>
      <Stack>
        {!hideHeader && (
          <>
            <Flex alignItems="center" gap={2} justifyContent="space-between">
              <NoveltyBadge date={setSalaryValueByKey('created_at')} />

              <Flex alignItems="center" gap={2}>
                <OwnerIcon entity={salary} />
                {SalaryFilterFields.includes('status') && <Status status={setSalaryValueByKey('status') as string} />}
              </Flex>
            </Flex>
            <Divider color="borderColor" />
          </>
        )}
        <Flex flexWrap="wrap" justify="space-between" gap={2} width="100%">
          <HStack width="100%">
            <CustomIconViewer
              variant="twoTone"
              boxSize={8}
              color={setSalaryValueByKey('color')}
              icon={setSalaryValueByKey('icon')}
            />

            <Stack spacing={0} width="100%">
              <TextWithCharacterLimit hasHighlight highlightQuery={setSalaryFilterValueByKey('name')}>
                {setSalaryValueByKey('name')}
              </TextWithCharacterLimit>

              <Flex align="center" justify="space-between" gap={2}>
                <TextWithCharacterLimit
                  characterLimit={25}
                  hasHighlight
                  highlightQuery={setSalaryFilterValueByKey('description')}
                  variant="secondary"
                >
                  {setSalaryValueByKey('description')}
                </TextWithCharacterLimit>

                <TextWithCharacterLimit fontSize="xs" color="primary">
                  {`${centsToReais(Number(setSalaryValueByKey('amount')))}`}
                </TextWithCharacterLimit>
              </Flex>
            </Stack>
          </HStack>
        </Flex>

        {children && <Divider color="borderColor" />}

        {children}
      </Stack>
    </Card>
  )
}

export default memo(SalaryCard)
